import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push, replace } from 'connected-react-router';

import { getLocation } from 'data_system/resource_portal/selectors';
import { getUserId, getResErrors } from 'data_system/token_auth/selectors';
import {
  setHeadersFromLocalStorage,
  deleteHeaders,
} from 'data_system/token_auth/utils';

import { PORTAL_DISABLED } from 'ui_system/token_auth/routes';

import {
  VALIDATE_TOKEN_REQUEST,
  LOGIN_REQUEST,
  RESET_PASSWORD_REQUEST,
  LOGOUT_REQUEST,
  LOGIN,
  LOGOUT,
  AUTH_ERRORS,
  SESSION_TIMEOUT,
  SUBMIT_COMPLETED,
} from 'data_system/token_auth/actionTypes';
import { validateTokenComplete } from 'data_system/token_auth/actions';
import Api from 'data_system/resource_portal/api';
import { createSagas } from 'data_system/token_auth/createSagas';

const sharedSagas = createSagas({ Api });

// Make sure a resource signed into a certain partner's resource portal
// cannot sign into a different resource portal for a different partner
const verifyPartnerResource = (resource, partnerId) => {
  const { userId } = resource.attributes;
  return userId === partnerId;
};

// Only fired when form is submitted by the user
// Skipped when "Logged In" via a validated token from a previous session
function* loginUser({ params }) {
  try {
    const { email, rememberMe } = params;
    const location = yield select(getLocation);
    const partnerId = yield select(getUserId);
    const res = yield call(Api.loginUser, params);

    // Save their email for when they come back to the login page.
    if (rememberMe) {
      localStorage.setItem('email', email);
    } else {
      localStorage.removeItem('email');
    }

    if (verifyPartnerResource(res.data.data.data, partnerId)) {
      yield put({ type: LOGIN, res });
      const { from } = location.state || { from: { pathname: '/' } };
      yield put(replace(from.pathname));
    }
  } catch (res) {
    if (res.response.status === 403) {
      yield put(push(PORTAL_DISABLED));
      yield put({ type: SUBMIT_COMPLETED });
    } else {
      yield put({ type: AUTH_ERRORS, errors: getResErrors(res) });
    }
  }
}

function* resetPassword({ params }) {
  try {
    const res = yield call(Api.resetPassword, params);
    yield put({ type: LOGIN, res });
  } catch (res) {
    yield put({ type: AUTH_ERRORS, errors: getResErrors(res) });
  }
}

function* validateToken() {
  try {
    setHeadersFromLocalStorage();
    const location = yield select(getLocation);
    const partnerId = yield select(getUserId);
    const res = yield call(Api.validateToken);

    if (verifyPartnerResource(res.data.data.data, partnerId)) {
      yield put({ type: LOGIN, res });
      const { pathname } = location || { pathname: '/' };
      yield put(replace(pathname));
    }

    yield put(validateTokenComplete);
  } catch (res) {
    yield put(validateTokenComplete);
    deleteHeaders();
  }
}

function* logoutUser({ type }) {
  try {
    yield call(Api.logoutUser);
    deleteHeaders();
    // If it's SESSION_TIMEOUT, let user click on timeout modal before deleting the user in Redux
    if (type === LOGOUT_REQUEST) yield put({ type: LOGOUT });
  } catch (res) {
    yield put({ type: AUTH_ERRORS, errors: getResErrors(res) });
  }
}

export const authSagas = [
  ...sharedSagas,
  takeLatest(LOGIN_REQUEST, loginUser),
  takeLatest(RESET_PASSWORD_REQUEST, resetPassword),
  takeLatest(VALIDATE_TOKEN_REQUEST, validateToken),
  takeLatest(LOGOUT_REQUEST, logoutUser),
  takeLatest(SESSION_TIMEOUT, logoutUser),
];
